// API service for Vivaria backend

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://vivaria-api.josebreviljr.workers.dev';

export interface User {
  id: number;
  username: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  last_login?: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface Conversation {
  id: string;
  user_id: number;
  title?: string;
  conversation_type: 'soap_notes' | 'chat' | 'mixed';
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  original_text?: string;
  anonymized_text?: string;
  analysis?: string;
  message_type: 'soap_note' | 'chat' | 'system';
  timestamp: string;
  metadata?: any;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class APIService {
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on initialization
    this.token = localStorage.getItem('vivaria_token');
  }

  setAuthToken(token: string) {
    this.token = token;
    localStorage.setItem('vivaria_token', token);
  }

  clearAuthToken() {
    this.token = null;
    localStorage.removeItem('vivaria_token');
  }

  getAuthToken(): string | null {
    return this.token;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data: ApiResponse<T> = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'API request failed');
    }

    return data.data as T;
  }

  // Authentication methods
  async login(username: string, password: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    
    this.setAuthToken(response.token);
    return response;
  }

  async verifyToken(): Promise<{ user: User }> {
    return await this.request<{ user: User }>('/api/auth/verify');
  }

  async logout(): Promise<void> {
    this.clearAuthToken();
  }

  // Conversation methods
  async getConversations(limit: number = 50, offset: number = 0, search?: string): Promise<{ conversations: Conversation[] }> {
    const params = new URLSearchParams({ 
      limit: limit.toString(), 
      offset: offset.toString() 
    });
    
    if (search) {
      params.append('search', search);
    }

    return await this.request<{ conversations: Conversation[] }>(`/api/conversations?${params}`);
  }

  async createConversation(title?: string, conversation_type?: string): Promise<{ conversation: Conversation }> {
    return await this.request<{ conversation: Conversation }>('/api/conversations', {
      method: 'POST',
      body: JSON.stringify({ title, conversation_type }),
    });
  }

  async updateConversation(id: string, updates: Partial<Conversation>): Promise<{ conversation: Conversation }> {
    return await this.request<{ conversation: Conversation }>(`/api/conversations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteConversation(id: string): Promise<void> {
    await this.request(`/api/conversations/${id}`, {
      method: 'DELETE',
    });
  }

  // Message methods
  async getMessages(conversationId: string, limit: number = 100, offset: number = 0): Promise<{ messages: Message[] }> {
    const params = new URLSearchParams({ 
      limit: limit.toString(), 
      offset: offset.toString() 
    });

    return await this.request<{ messages: Message[] }>(`/api/conversations/${conversationId}/messages?${params}`);
  }

  async createMessage(messageData: {
    conversation_id: string;
    role: 'user' | 'assistant';
    original_text?: string;
    anonymized_text?: string;
    analysis?: string;
    message_type: 'soap_note' | 'chat' | 'system';
    metadata?: any;
  }): Promise<{ message: Message }> {
    return await this.request<{ message: Message }>('/api/messages', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  }

  // Admin methods (only accessible to admin users)
  async getUsers(): Promise<{ users: User[] }> {
    return await this.request<{ users: User[] }>('/api/admin/users');
  }

  async createUser(username: string, password: string): Promise<{ user: User }> {
    return await this.request<{ user: User }>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  async updateUser(userId: number, updates: { username?: string; password?: string; is_active?: boolean }): Promise<{ user: User }> {
    return await this.request<{ user: User }>(`/api/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteUser(userId: number): Promise<void> {
    await this.request(`/api/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Health check
  async healthCheck(): Promise<{ message: string; timestamp: string }> {
    return await this.request<{ message: string; timestamp: string }>('/api/health');
  }

  // AI Analysis methods
  async analyzeText(text: string, messageType: 'soap_note' | 'chat', provider: 'openai' | 'gemini' = 'openai'): Promise<{ analysis: string }> {
    return await this.request<{ analysis: string }>('/api/ai/analyze', {
      method: 'POST',
      body: JSON.stringify({ 
        text, 
        message_type: messageType, 
        provider 
      }),
    });
  }

  // Setup admin user (one-time setup)
  async setupAdmin(adminPassword: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminPassword }),
    });

    const data: ApiResponse<{ message: string }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Setup failed');
    }

    return data.data as { message: string };
  }
}

export const apiService = new APIService();